import React, { useState } from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const MILESTONES = [
  { labelFr: 'Naissance', labelAr: 'الولادة', months: 0,
    vaccinesFr: ['BCG', 'Hépatite B (1ère dose)'],
    vaccinesAr: ['السل (BCG)', 'التهاب الكبد B (الجرعة الأولى)'] },
  { labelFr: '2 mois', labelAr: 'شهرين', months: 2,
    vaccinesFr: ['Pentavalent/hexavalent (DTC-Hib-HepB)', 'Polio', 'Pneumocoque', 'Rotavirus (selon disponibilité)'],
    vaccinesAr: ['اللقاح الخماسي/السداسي (DTC-Hib-HepB)', 'شلل الأطفال', 'المكورات الرئوية', 'الروتا فيروس (حسب التوفر)'] },
  { labelFr: '3 mois', labelAr: '3 أشهر', months: 3,
    vaccinesFr: ['Pentavalent/hexavalent (DTC-Hib-HepB)', 'Polio'],
    vaccinesAr: ['اللقاح الخماسي/السداسي (DTC-Hib-HepB)', 'شلل الأطفال'] },
  { labelFr: '4 mois', labelAr: '4 أشهر', months: 4,
    vaccinesFr: ['Pentavalent/hexavalent (DTC-Hib-HepB)', 'Polio', 'Pneumocoque', 'Rotavirus (selon disponibilité)'],
    vaccinesAr: ['اللقاح الخماسي/السداسي (DTC-Hib-HepB)', 'شلل الأطفال', 'المكورات الرئوية', 'الروتا فيروس (حسب التوفر)'] },
  { labelFr: '11 mois', labelAr: '11 شهرًا', months: 11,
    vaccinesFr: ['ROR (rougeole-oreillons-rubéole)'],
    vaccinesAr: ['اللقاح الثلاثي الفيروسي (حصبة-نكاف-حصبة ألمانية)'] },
  { labelFr: '12 mois', labelAr: '12 شهرًا', months: 12,
    vaccinesFr: ['Pneumocoque (rappel)'],
    vaccinesAr: ['المكورات الرئوية (جرعة منشطة)'] },
  { labelFr: '18 mois', labelAr: '18 شهرًا', months: 18,
    vaccinesFr: ['Rappel DTC-Hib-HepB', 'Rappel polio', 'Rappel ROR'],
    vaccinesAr: ['جرعة منشطة للقاح DTC-Hib-HepB', 'جرعة منشطة لشلل الأطفال', 'جرعة منشطة للثلاثي الفيروسي'] },
  { labelFr: '6 ans', labelAr: '6 سنوات', months: 72,
    vaccinesFr: ['DTC/DT (rappel)', 'Polio (rappel)', 'ROR (selon indication)'],
    vaccinesAr: ['DTC/DT (جرعة منشطة)', 'شلل الأطفال (جرعة منشطة)', 'الثلاثي الفيروسي (حسب الحاجة)'] },
  { labelFr: '11-13 ans', labelAr: '11-13 سنة', months: 132,
    vaccinesFr: ['dT (rappel)', 'HPV (selon sexe, âge et réglementation en vigueur)'],
    vaccinesAr: ['dT (جرعة منشطة)', 'فيروس الورم الحليمي البشري HPV (حسب الجنس والعمر والتنظيم المعمول به)'] },
];

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
};

const formatDate = (date, isRTL) =>
  date.toLocaleDateString(isRTL ? 'ar-DZ-u-nu-latn' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const today = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

const VaccinationCalendar = () => {
  const { t, isRTL } = useLanguage();
  const [birthDate, setBirthDate] = useState('');
  const dob = parseDate(birthDate);
  const now = today();

  return (
    <ToolPageLayout
      title={t('vaccination_title')}
      subtitle={t('vaccination_subtitle')}
      specialty="pediatre"
    >
      <div>
        <Label className="mb-1 block">{t('vaccination_dob_label')}</Label>
        <Input
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
          className="rounded-xl max-w-xs"
        />
      </div>

      <div className="mt-8 space-y-3">
        {MILESTONES.map((m, i) => {
          const date = dob ? addMonths(dob, m.months) : null;
          const done = date ? date <= now : false;
          const label = isRTL ? m.labelAr : m.labelFr;
          const vaccines = isRTL ? m.vaccinesAr : m.vaccinesFr;
          return (
            <div
              key={i}
              className={`rounded-2xl p-5 border flex gap-4 ${
                done ? 'bg-[#F0FDF4] border-[#10B981]/30' : 'bg-[#F8FAFC] border-[#E2E8F0]'
              }`}
            >
              <div className="shrink-0 mt-0.5">
                {done ? (
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                ) : (
                  <Circle className="w-5 h-5 text-[#94A3B8]" />
                )}
              </div>
              <div>
                <div className="font-semibold text-[#0F172A]">
                  {label}
                  {date && <span className="text-[#64748B] font-normal"> — {formatDate(date, isRTL)}</span>}
                </div>
                <ul className="mt-1 text-sm text-[#64748B] list-disc list-inside">
                  {vaccines.map((v, j) => (
                    <li key={j}>{v}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-sm text-[#64748B]">
        {t('vaccination_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default VaccinationCalendar;
